import {
  Actions,
  AnyObject,
  Async,
  F1,
  SMap,
  TMap,
} from '@client/new/helpers/types';
import {
  DayName,
  FixedTitle,
  LiteWorkout,
  RawWorkout,
  WeekName,
  WorkoutsModel,
  WorkoutType,
} from './workouts.domain';
import { useState } from 'react';
import { CollectionProgram } from '../../../../components/collections/collections.types';
import { useLiteProgram } from '../useLiteProgram';
import { toTitle, useProgramWithWorkouts } from './workouts.hooks';
import ajax, { reorderProgram } from '../../../../components/ajax';
import { makeAsync } from '../../../helpers/async';
import { liteWorkout } from '../../collections/mocks';
import { values } from '../../../helpers/general';
import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import { useGetWorkoutDeepLinkQuery } from './workout.query';

export type UseWorkoutsModelParams = {
  collection: CollectionProgram;
  creatorId?: number;
  readonly: boolean;
};
type WorkoutsModelActionsMap = {
  reorder: Array<number>;
  rename: Pick<LiteWorkout, 'week' | 'day' | 'workoutId'>;
  remove: Pick<LiteWorkout, 'workoutId'>;
  archive: Pick<LiteWorkout, 'workoutId'>;
  publish: Pick<LiteWorkout, 'workoutId'>;
  duplicate: LiteWorkout;
  getDeepLink: number;
};
export type WorkoutsModelActions = Actions<WorkoutsModelActionsMap>;

export type WorkoutsModelDispatcher = F1<WorkoutsModelActions, number>;

const useExtendedState = <T extends AnyObject>(
  v: T,
): [
  T,
  F1<Partial<T> | F1<T, Partial<T>>, null>,
  React.Dispatch<React.SetStateAction<T>>,
] => {
  const [state, setState] = useState(v);
  const setExtendedState = (delta: Partial<T> | F1<T, Partial<T>>) => {
    setState((prev) => ({
      ...prev,
      ...(typeof delta === 'function' ? delta(prev) : delta),
    }));
    return null;
  };
  return [state, setExtendedState, setState];
};

export const useWorkoutsModel = (
  params: UseWorkoutsModelParams,
  selectedList: WorkoutType,
): Async<WorkoutsModel & { dispatch: WorkoutsModelDispatcher }> => {
  const asyncInfluencer = useInfluencer(
    params.creatorId ? Number(params.creatorId) : null,
  );

  const [workoutDeepLink, setWorkoutDeepLink] = useState<
    Async<{ url: string }>
  >({
    type: 'not-fetched',
  });
  const asyncProgram = useLiteProgram(params.collection);

  const [workoutsStateMap, extendWorkoutsStateMap, setWorkoutsStateMap] =
    useExtendedState<WorkoutsModel['workoutsStateMap']>({});
  type State = {
    cloningWorkoutTmpIds: number[];
    publishedOrderIds: Array<number> | null;
    changedWorkouts: SMap<LiteWorkout>;
    actionResults: TMap<number, Async>;
  };

  const [state, setState] = useExtendedState<State>({
    actionResults: {},
    changedWorkouts: {},
    cloningWorkoutTmpIds: [],
    publishedOrderIds: null,
  });

  const withActionResult = <T>(cb: (id: number) => Promise<T>) => {
    const id = new Date().getTime();

    const setResult = (v: Async) => {
      setState((prev) => ({
        actionResults: { ...prev.actionResults, [id]: v },
      }));
    };

    setResult(makeAsync.fetching());

    cb(id)
      .then(() => setResult(makeAsync.fetched(id)))
      .catch((err) => setResult(makeAsync.error(err)));
    return id;
  };

  const { mutateAsync: getWorkoutDeepLink } = useGetWorkoutDeepLinkQuery();

  const programId = params.collection.id;
  const creatorId = params.collection.influencerId;
  const programWithWorkouts = useProgramWithWorkouts({
    creatorId,
    programId,
    selectedList,
  });

  if (programWithWorkouts.type !== 'fetched') return programWithWorkouts;
  if (asyncInfluencer.type !== 'fetched') return asyncInfluencer;
  if (asyncProgram.type !== 'fetched') return asyncProgram;

  const workoutsMap = values(programWithWorkouts.value.workouts)
    .flat()
    .reduce(
      (acc, w) => ({ ...acc, [w.workoutId]: w }),
      {} as SMap<LiteWorkout>,
    );

  const refetchData = () => {
    programWithWorkouts.value
      .refetch()
      .then(() => {
        setState({ cloningWorkoutTmpIds: [] });
        setWorkoutsStateMap({});
      })
      .catch(() => {
        setState({ cloningWorkoutTmpIds: [] });
        setWorkoutsStateMap({});
      });
  };

  const workoutAPICallsMap = {
    archive: ajax.archiveWorkout,
    remove: ajax.deleteWorkout,
    publish: (workoutId: number) => {
      return ajax.getWorkout(workoutId).then((data: any) => {
        if (!data) return;
        return ajax.upsertWorkout({
          ...data,
          scheduledAt: undefined,
          publishedAt: new Date().toISOString(),
          id: workoutId,
          isVisible: true,
        });
      });
    },
  };

  type ActionCb<T extends keyof WorkoutsModelActionsMap> = (
    actionId: number,

    payload: WorkoutsModelActionsMap[T],
  ) => Promise<unknown>;

  const clone: ActionCb<'duplicate'> = (actionId, w) => {
    setState((prev) => ({
      cloningWorkoutTmpIds: [...prev.cloningWorkoutTmpIds, actionId],
    }));

    return ajax
      .cloneWorkout(
        w.workoutId,
        w.programId
          ? { programId: w.programId, name: w.orgTitle + ' copy' }
          : undefined,
      )
      .then(refetchData);
  };

  const apiAction =
    (type: keyof typeof workoutAPICallsMap): ActionCb<'archive'> =>
    (actionId, { workoutId }) => {
      extendWorkoutsStateMap({ [workoutId]: makeAsync.fetching() });
      return workoutAPICallsMap[type](workoutId)
        .then(refetchData)
        .catch((e) =>
          extendWorkoutsStateMap({ [workoutId]: makeAsync.error(e) }),
        );
    };

  const reorderWorkouts: ActionCb<'reorder'> = (actionId, workoutIds) => {
    setState((prev) => ({
      publishedOrderIds: workoutIds,
      actionResults: {
        ...prev.actionResults,
        [actionId]: makeAsync.fetching(),
      },
    }));
    return reorderProgram(programId, workoutIds);
  };

  const renameWorkout: ActionCb<'rename'> = async (
    actionId,
    { workoutId, ...delta },
  ) => {
    const workout = workoutsMap[workoutId];
    const day = delta.day || workout.day || workout.orgDay || ('D1' as DayName);
    const week =
      delta.week || workout.week || workout.orgWeek || ('W1' as WeekName);
    const orgTitle = toTitle(workout, { day, week });
    workout.day = day;
    workout.week = week;
    workout.orgTitle = orgTitle;

    setState((prev) => ({
      changedWorkouts: {
        ...prev.changedWorkouts,
        [workout.workoutId]: workout,
      },
    }));

    const { published } = programWithWorkouts.value.workouts;
    const newIds = published
      .sort((l, r) => l.orgTitle.localeCompare(r.orgTitle))
      .map((w) => w.workoutId);

    await reorderWorkouts(actionId, newIds);
    const data: any = await ajax.getWorkout(workoutId);
    return ajax.upsertWorkout({
      ...data,
      id: workoutId,
      name: orgTitle,
    } as RawWorkout);
  };

  const dispatch: WorkoutsModelDispatcher = (action) =>
    withActionResult((actionId: number) => {
      switch (action.type) {
        case 'reorder':
          return reorderWorkouts(actionId, action.payload);
        case 'publish':
        case 'archive':
        case 'remove':
          return apiAction(action.type)(actionId, action.payload);
        case 'duplicate':
          return clone(actionId, action.payload);
        case 'rename':
          return renameWorkout(actionId, action.payload);
        case 'getDeepLink':
          setWorkoutDeepLink({ type: 'fetching' });
          return getWorkoutDeepLink(action.payload)
            .then((v) => {
              setWorkoutDeepLink({ type: 'fetched', value: v });
            })
            .catch((e) => {
              setWorkoutDeepLink({ type: 'fetch-error', value: e });
            });
      }
    });

  if (state.publishedOrderIds) {
    programWithWorkouts.value.workouts.published = state.publishedOrderIds.map(
      (id) => state.changedWorkouts[id] || workoutsMap[id],
    );
  }

  const statesMap = { ...workoutsStateMap };
  if (state.cloningWorkoutTmpIds.length) {
    const tmpWorkouts = state.cloningWorkoutTmpIds.map((workoutId) =>
      liteWorkout({
        workoutId,
        type: 'draft',
        orgTitle: '',
        fixedTitle: '' as FixedTitle,
      }),
    );

    programWithWorkouts.value.workouts.draft = [
      ...tmpWorkouts,
      ...programWithWorkouts.value.workouts.draft,
    ];
    state.cloningWorkoutTmpIds.forEach(
      (id) => (statesMap[id] = makeAsync.fetching()),
    );
  }

  const isWorkoutArchiveEnabled = asyncProgram.value.accessType === 'premium';

  return {
    type: 'fetched',
    value: {
      ...programWithWorkouts.value,
      workoutDeepLink,
      workoutsStateMap: statesMap,
      dispatch,
      influencerId: asyncInfluencer.value.influencerId,
      actionResults: state.actionResults,
      readonly: params.readonly,
      isWorkoutArchiveEnabled,
    },
  };
};
